import React, { useState, useEffect } from 'react';
import {
  Package2,
  Search,
  MapPin,
  CalendarClock,
  Truck,
  CheckCircle2,
  Circle,
  Package,
  IndianRupee,
  Clock,
  User,
  Box,
  MapPinned,
  Calendar,
} from 'lucide-react';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import axios from 'axios';
import { Base_Url, b2bTrackShipment } from '../../../config/config';

const B2BTrackShipment = () => {
  const [waybillNumber, setWaybillNumber] = useState('');
  const [orderTrackingInfo, setOrderTrackingInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { isDarkMode, isToggleSidebar, orderid, setorderId } = useMyContext();

  useEffect(() => {
    if (orderid) {
      handleTrack();
    }
  }, [orderid]);

  const handleTrack = async () => {
    const trackingNumber = orderid || waybillNumber;
    if (!trackingNumber) {
      setError('Please enter a valid order ID');
      setOrderTrackingInfo(null);
      return;
    }

    setIsLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    try {
      const response = await axios.post(
        Base_Url + b2bTrackShipment,
        {
          order_id: trackingNumber,
          shipment_type: 'Forward',
        },
        {
          headers: {
            token: token,
            cus_id: cus_id,
            'Content-Type': 'application/json',
          },
        }
      );

      const shipmentData = response.data.data.ShipmentData[0]?.Shipment;
      setOrderTrackingInfo(shipmentData || null);
      setError('');
      setorderId(null);
    } catch (err) {
      setError('Failed to fetch tracking information. Please try again.');
      setOrderTrackingInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Available';
    return new Date(dateString).toLocaleString();
  };

  const DetailRow = ({ label, value, icon: Icon }) => (
    <div className={`flex items-center justify-between py-1.5 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
      <div className="flex items-center space-x-1.5">
        {Icon && <Icon className={`w-3 h-3 ${isDarkMode ? 'text-white/40' : 'text-black/40'}`} />}
        <span className={`text-[9px] ${isDarkMode ? 'text-white/50' : 'text-black/50'}`}>{label}</span>
      </div>
      <span className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>{value || 'Not Available'}</span>
    </div>
  );

  const getStepIcon = (scan, isLatest) => {
    const status = scan.status?.toLowerCase();
    if (status) {
      return <CheckCircle2 className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} />;
    }
    if (isLatest) {
      return (
        <div className="relative inline-flex items-center justify-center w-6 h-6 rounded-full border ${isDarkMode ? 'border-white/20' : 'border-black/20'} animate-pulse">
          <Truck className={`w-3 h-3 ${isDarkMode ? 'text-white' : 'text-black'}`} />
        </div>
      );
    }
    return <Circle className={`w-4 h-4 ${isDarkMode ? 'text-white/30' : 'text-black/30'}`} />;
  };

  const DeliveryInfoPanel = () => (
    <div className="space-y-3">
      {/* Shipment Overview Card */}
      <div className={`border rounded-lg ${isDarkMode ? 'border-white/10 bg-black' : 'border-black/10 bg-white'}`}>
        <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
          <h2 className={`text-[10px] font-semibold flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <Package2 className="w-3.5 h-3.5" />
            Shipment Overview
          </h2>
        </div>
        <div className="px-3 py-2 space-y-1">
          <DetailRow label="AWB Number" value={orderTrackingInfo?.AWB} icon={Package} />
          <DetailRow label="Waybill no" value={orderTrackingInfo?.ReferenceNo} icon={Package2} />
          <DetailRow label="Order Type" value={orderTrackingInfo?.OrderType} icon={Box} />
          <DetailRow
            label="Invoice Amount"
            value={`₹${orderTrackingInfo?.InvoiceAmount || 0}`}
            icon={IndianRupee}
          />
          <DetailRow
            label="COD Amount"
            value={`₹${orderTrackingInfo?.CODAmount || 0}`}
            icon={IndianRupee}
          />
          <DetailRow label="Quantity" value={orderTrackingInfo?.Quantity} icon={Box} />
        </div>
      </div>

      {/* Location Details */}
      <div className={`border rounded-lg ${isDarkMode ? 'border-white/10 bg-black' : 'border-black/10 bg-white'}`}>
        <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
          <h2 className={`text-[10px] font-semibold flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <MapPinned className="w-3.5 h-3.5" />
            Location Details
          </h2>
        </div>
        <div className="px-3 py-2 space-y-1">
          <DetailRow label="Origin" value={orderTrackingInfo?.Origin} icon={MapPin} />
          <DetailRow
            label="Destination"
            value={orderTrackingInfo?.Destination || 'Not specified'}
            icon={MapPin}
          />
          <DetailRow label="Pickup Location" value={orderTrackingInfo?.PickupLocation} icon={MapPin} />
        </div>
      </div>

      {/* Consignee Details */}
      <div className={`border rounded-lg ${isDarkMode ? 'border-white/10 bg-black' : 'border-black/10 bg-white'}`}>
        <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
          <h2 className={`text-[10px] font-semibold flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <User className="w-3.5 h-3.5" />
            Consignee Details
          </h2>
        </div>
        <div className="px-3 py-2 space-y-1">
          {orderTrackingInfo?.Consignee && (
            <>
              <DetailRow label="Name" value={orderTrackingInfo.Consignee.Name} icon={User} />
              <DetailRow
                label="City"
                value={orderTrackingInfo.Consignee.City || orderTrackingInfo.Consignee.State}
                icon={MapPin}
              />
              <DetailRow label="Pin Code" value={orderTrackingInfo.Consignee.PinCode} icon={MapPin} />
              <DetailRow label="Country" value={orderTrackingInfo.Consignee.Country} icon={MapPin} />
            </>
          )}
        </div>
      </div>

      {/* Dates Information */}
      <div className={`border rounded-lg ${isDarkMode ? 'border-white/10 bg-black' : 'border-black/10 bg-white'}`}>
        <div className={`px-3 py-2 border-b ${isDarkMode ? 'border-white/10' : 'border-black/10'}`}>
          <h2 className={`text-[10px] font-semibold flex items-center gap-1.5 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <Calendar className="w-3.5 h-3.5" />
            Important Dates
          </h2>
        </div>
        <div className="px-3 py-2 space-y-1">
          <DetailRow label="Pickup Date" value={formatDate(orderTrackingInfo?.PickUpDate)} icon={Calendar} />
          <DetailRow
            label="Expected Delivery"
            value={formatDate(orderTrackingInfo?.ExpectedDeliveryDate)}
            icon={Calendar}
          />
          <DetailRow
            label="First Attempt Date"
            value={formatDate(orderTrackingInfo?.FirstAttemptDate)}
            icon={Calendar}
          />
        </div>
      </div>

      {/* Status Banner */}
      <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/5'}`}>
        <div className="flex items-center space-x-2 mb-1.5">
          <Clock className={`w-3.5 h-3.5 ${isDarkMode ? 'text-white' : 'text-black'}`} />
          <h3 className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>Last Scan</h3>
        </div>
        <div className="space-y-1">
          <p className={`text-[10px] font-medium ${isDarkMode ? 'text-white/80' : 'text-black/80'}`}>
            {orderTrackingInfo?.Status?.StatusLocation}
          </p>
          <p className={`text-[9px] ${isDarkMode ? 'text-white/40' : 'text-black/40'}`}>
            Last Updated: {formatDate(orderTrackingInfo?.Status?.StatusDateTime)}
          </p>
        </div>
      </div>
    </div>
  );
  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";
  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'}`}
    >
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

      <NavBar />

      <div className="flex flex-1 overflow-hidden">

        {isToggleSidebar && (
          <div className={`w-64 m-3 rounded-3xl ${glass} overflow-hidden`}>
            <Sidebar />
          </div>
        )}
        <div className={`flex-1 p-3 md:p-4 overflow-auto ${isDarkMode ? 'bg-black' : 'bg-gray-100'}`}>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 max-w-7xl mx-auto">
            {/* Main Tracking Section */}
            <div className={`lg:col-span-2 border rounded-lg ${isDarkMode ? 'border-white/10 bg-black' : 'border-black/10 bg-white'}`}>
              <div className="p-4">
                <div className="flex items-center space-x-1.5 mb-3">
                  <Package2 color="blue" className={`w-4 h-4 ${isDarkMode ? 'text-white' : 'text-black'}`} />
                  <h1 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>
                    Track Your Shipment
                  </h1>
                </div>
                <div className="space-y-3">
                  <div className="flex space-x-1.5">
                    <input
                      type="text"
                      placeholder="Enter Order ID"
                      value={waybillNumber}
                      onChange={(e) => setWaybillNumber(e.target.value)}
                      className={`flex-1 px-2.5 py-1.5 text-[10px] border rounded-lg focus:outline-none focus:ring-1 ${isDarkMode ? 'bg-black border-white/20 text-white focus:ring-white/50 placeholder-white/30' : 'bg-white border-black/20 text-black focus:ring-black/50 placeholder-black/30'}`}
                    />
                    <button
                      onClick={handleTrack}
                      disabled={isLoading}
                      className={`px-3 py-1.5 text-[10px] font-medium rounded-lg transition-colors hover:opacity-85 disabled:opacity-50 flex items-center space-x-1.5 ${isDarkMode ? 'bg-white text-black' : 'bg-blue-600 text-white'}`}
                    >
                      {isLoading ? (
                        <div className={`w-3.5 h-3.5 border-2 ${isDarkMode ? 'border-black' : 'border-white'} border-t-transparent rounded-full animate-spin`} />
                      ) : (
                        <>
                          <Search className="w-3 h-3" />
                          <span>Track</span>
                        </>
                      )}
                    </button>
                  </div>

                  {error && (
                    <div className={`p-2.5 border-l-4 rounded ${isDarkMode ? 'border-white/50 bg-white/5 text-white/70' : 'border-black/50 bg-black/5 text-black/70'}`}>
                      <span className="text-[10px]">{error}</span>
                    </div>
                  )}

                  {orderTrackingInfo && (
                    <div className="space-y-4 mt-4">
                      {orderTrackingInfo.scans?.map((scan, index) => (
                        <div key={index} className="relative">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-0.5">{getStepIcon(scan, index === 0)}</div>
                            <div className="flex-1 space-y-0.5">
                              <p className={`text-[10px] font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>{scan.status}</p>
                              <p className={`text-[8px] ${isDarkMode ? 'text-white/40' : 'text-black/40'}`}>{scan.scan_remark}</p>
                            </div>
                          </div>
                          {index < orderTrackingInfo.scans.length - 1 && (
                            <div className={`absolute left-[7px] top-5 w-px h-10 ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info Panel */}
            <div className="lg:col-span-3">
              <DeliveryInfoPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BTrackShipment;